<script setup lang="ts">
import { groupProducts, productsSchema } from '~/utils/crystallize/product'

definePageMeta({
  // The layout here is derived from dynamic data, so we use NuxtLayout directly in the template
  layout: false,
  colorMode: 'light',
  theme: 'blurple',
  name: 'product',
})

const route = useRoute<'product'>()

const { data: product, suspense: waitForProduct } = useProduct(() => ensureStringParam(route.params.slug))

await waitForProduct()

if (!product.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Produktet finnes ikke',
    fatal: true,
    data: {
      navigationContext: 'marketing' satisfies NavigationContext,
    },
  })
}

const card = useProductCard(product as Ref<CrystallizeProduct>)

const options = useCatalogueQueryOptions({
  path: '/produktkatalog',
  schema: productsSchema,
})

const { data: isHighlighted, suspense: catalogue } = useQuery({
  ...options,
  select(data) {
    const grouped = groupProducts(data)
    return grouped.highlighted?.id === product.value?.id
  },
})

await catalogue()

useHead({
  title: computed(() => card.value.title),
  bodyAttrs: {
    class: isHighlighted.value ? 'bg-blurple-500' : 'bg-white',
  },
})

const description = computed(() => getTextContent(card.value?.variant?.components?.description))

useSeoMeta({
  ogTitle: computed(() => card.value.title),
  description: computed(() => card.value.subtitle ?? description.value),
  ogDescription: description,
})
</script>

<template>
  <NuxtLayout :name="isHighlighted ? 'nettbutikk-dark' : 'nettbutikk'">
    <article v-if="product" class="overflow-x-clip">
      <section class="pt-3xl pb-2xl">
        <NettbutikkProductHeader :product="card" wrapper-class="gap-s" :highlighted="isHighlighted ?? false">
          <CrystallizeImage class="hidden md:block md:col-span-1 lg:col-span-3 2xl:col-start-9 max-w-full" :image="card.illustration" img-class="w-[320px]" />
        </NettbutikkProductHeader>
      </section>

      <section class="pt-xl pb-2xl" :class="isHighlighted ? 'bg-polar-500' : 'bg-blurple-50'">
        <NettbutikkProductIncludedList
          :list="product.components.includedList"
          :text="product.components.includedText"
          :highlighted="isHighlighted ?? false"
        >
          <NettbutikkProductVerticalCard
            v-if="card.variant"
            :title="card.title"
            :variant="card.variant"
            class="col-span-4 md:col-span-2 md:col-start-2 lg:col-span-3 2xl:col-span-4"
          />
        </NettbutikkProductIncludedList>
      </section>

      <NettbutikkProductQuote
        v-if="product.components?.quote"
        :data="product.components.quote"
        class="bg-blurple-500 py-5xl"
      />

      <section
        v-if="product.components?.legalCommentary?.title"
        class="py-5xl"
        :class="isHighlighted ? 'bg-blurple-100' : 'bg-carbon-100'"
      >
        <NettbutikkProductMetadataSection
          :data="product.components.legalCommentary"
          dir="ltr"
          frame-shape="decagon"
        />
      </section>

      <NettbutikkProductLiterature
        v-for="(literature, lKey) in product.components.literature"
        :key="lKey"
        class="bg-carbon-50 py-5xl"
        :data="literature"
      />

      <section
        v-if="product.components?.journals?.title"
        class="py-5xl"
        :class="isHighlighted ? 'bg-blurple-50' : 'bg-white'"
      >
        <!-- TODO: This should have a rounded image, but we don't support that yet -->
        <NettbutikkProductMetadataSection
          :data="product.components.journals"
          dir="rtl"
          frame-shape="hexagon"
        />
      </section>

      <section
        v-if="product.components?.templateMetadata?.title"
        class="py-5xl"
        :class="isHighlighted ? 'bg-blurple-200' : 'bg-blurple-50'"
      >
        <NettbutikkProductTemplates
          :data="product.components.templateMetadata"
          :templates="product.components.templates"
        />
      </section>

      <section
        v-if="card.variant"
        class="pt-xl pb-2xl"
        :class="isHighlighted ? 'bg-polar-500' : 'bg-blurple-100'"
      >
        <NettbutikkProductHeader :product="card" title-tag="h2" wrapper-class="gap-m md:pr-2xl" class="gap-s md:gap-m" :highlighted="isHighlighted ?? false">
          <NettbutikkProductVerticalCard
            :title="card.title"
            :variant="card.variant"
            class="col-span-4 md:col-span-2 md:col-start-2 lg:col-span-3 2xl:col-span-4"
          />
        </NettbutikkProductHeader>
      </section>

      <section v-if="isHighlighted" class="bg-blurple-500 pt-2xl pb-5xl text-white">
        <ContentGrid>
          <NettbutikkJuridikaCta class="col-span-4 lg:col-span-6 lg:col-start-2 2xl:col-start-4" />
        </ContentGrid>
      </section>

      <section class="bg-blurple-50 py-3xl">
        <NettbutikkProductRelatedProducts
          :data="product.components?.relatedProducts ?? []"
          :title="card.title"
          :self="product"
        />
      </section>
    </article>
  </NuxtLayout>
</template>
